import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Ruler, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FORMULA_TYPES = [
  { value: 'quantity_x_price', label: 'Quantidade x Preço Unitário', desc: 'Total = Qtd × Preço Unit.' },
  { value: 'fixed', label: 'Preço Fixo Mensal', desc: 'Total = Preço × Meses' },
  { value: 'hourly_rate', label: 'Valor por Hora (HST/PF)', desc: 'Total = Horas × Valor/Hora' },
  { value: 'per_stage', label: 'Valor por Etapa', desc: 'Total = Soma das Etapas' },
  { value: 'per_class', label: 'Valor por Turma', desc: 'Total = Turmas × Valor/Turma' }
];

const EMPTY_FORM = { name: '', abbreviation: '' };

const UnitsPage = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { loadUnits(); }, []);

  const loadUnits = async () => {
    try {
      const response = await axios.get(`${API_URL}/units-of-measure`, { withCredentials: true });
      setUnits(response.data);
    } catch (error) {
      toast.error('Erro ao carregar unidades');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setEditingUnit(null); setFormData({ ...EMPTY_FORM }); setDialogOpen(true); };

  const openEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({ name: unit.name, abbreviation: unit.abbreviation });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (editingUnit) {
        await axios.put(`${API_URL}/units-of-measure/${editingUnit.unit_id}`, formData, { withCredentials: true });
        toast.success('Unidade atualizada');
      } else {
        await axios.post(`${API_URL}/units-of-measure`, formData, { withCredentials: true });
        toast.success('Unidade criada');
      }
      setDialogOpen(false);
      loadUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (unitId) => {
    try {
      await axios.delete(`${API_URL}/units-of-measure/${unitId}`, { withCredentials: true });
      toast.success('Unidade excluída');
      setDeleteTarget(null);
      loadUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-12"><div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="units-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#111111]">Unidades de Medida</h1>
          <p className="text-[#6E6E73]">Cadastro de unidades de medida</p>
        </div>
        <Button onClick={openCreate} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="new-unit-button">
          <Plus className="w-4 h-4 mr-2" />Nova Unidade
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5EA] rounded-xl overflow-hidden">
        {units.length > 0 ? (
          <table className="w-full">
            <thead className="bg-[#F7F7F8]">
              <tr>
                <th className="text-left p-4 text-xs font-medium text-[#6E6E73]">UNIDADE</th>
                <th className="text-left p-4 text-xs font-medium text-[#6E6E73]">ABREVIACAO</th>
                <th className="text-right p-4 text-xs font-medium text-[#6E6E73]">ACOES</th>
              </tr>
            </thead>
            <tbody>
              {units.map((unit) => {
                return (
                  <tr key={unit.unit_id} className="border-t border-[#E5E5EA] hover:bg-[#F7F7F8]" data-testid={`unit-row-${unit.unit_id}`}>
                    <td className="p-4 text-sm font-medium">{unit.name}</td>
                    <td className="p-4 text-sm"><span className="bg-[#F7F7F8] px-2 py-1 rounded font-mono text-xs">{unit.abbreviation}</span></td>
                    <td className="p-4 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(unit)}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(unit)} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-12">
            <Ruler className="w-12 h-12 text-[#AEAEB2] mx-auto mb-3" />
            <p className="text-[#6E6E73] mb-4">Nenhuma unidade de medida cadastrada</p>
            <Button onClick={openCreate} className="bg-[#AF2628] hover:bg-[#961B22]"><Plus className="w-4 h-4 mr-2" />Criar Primeira</Button>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingUnit(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingUnit ? 'Editar Unidade' : 'Nova Unidade de Medida'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="unit-form">
            <div>
              <Label>Nome *</Label>
              <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Licença, Hora, Ponto de Função" data-testid="unit-name-input" />
            </div>
            <div>
              <Label>Abreviacao *</Label>
              <Input required value={formData.abbreviation} onChange={(e) => setFormData({ ...formData, abbreviation: e.target.value })} placeholder="Ex: un, h, PF, mes" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#AF2628] hover:bg-[#961B22]" disabled={submitting} data-testid="unit-submit-button">
                {submitting ? 'Salvando...' : (editingUnit ? 'Salvar' : 'Criar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-600" />Excluir Unidade</DialogTitle>
            <DialogDescription>Excluir a unidade <strong>{deleteTarget?.name}</strong>?</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(deleteTarget?.unit_id)}>Excluir</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnitsPage;
